import { CampoDeSelecao, CampoDeTexto, CampoDeTextoLongo } from '../../../components/campos'
import { NATUREZA_DA_SOLUCAO, ROTULO_CAUSA_EMERGENCIAL, ROTULO_PRIORIDADE, ROTULO_TIPO } from '../../../lib/glossario'
import { ROTULO_DO_CHAMADO } from './pendencias'
import type { PropsDosCampos } from './esquema'

interface Props extends PropsDosCampos {
  emergencial: boolean
}

/**
 * De onde a mudança vem e quanto ela corre.
 *
 * Os campos da emergência só aparecem quando a prioridade é emergencial. Mantê-los sempre visíveis
 * ensinaria que são opcionais — e eles são obrigatórios exatamente no caso em que aparecem
 * (item 8.31.5.2 do TR).
 *
 * @param props registro do formulário, erros e se a prioridade escolhida é emergencial
 * @returns os campos de procedência e prioridade
 */
export function CamposDeClassificacao({ register, errors, emergencial }: Props) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <CampoDeSelecao
        rotulo="De onde vem a mudança"
        ajuda="Projeto é trabalho planejado; chamado nasce de um atendimento no e-Niterói."
        erro={errors.tipo?.message}
        defaultValue=""
        {...register('tipo')}
      >
        <option value="" disabled>
          Escolha…
        </option>
        {Object.entries(ROTULO_TIPO).map(([valor, rotulo]) => (
          <option key={valor} value={valor}>
            {rotulo}
          </option>
        ))}
      </CampoDeSelecao>

      <CampoDeSelecao
        rotulo="Prioridade"
        ajuda="Emergencial é só para o que não pode esperar a próxima reunião do comitê."
        erro={errors.prioridade?.message}
        defaultValue=""
        {...register('prioridade')}
      >
        <option value="" disabled>
          Escolha…
        </option>
        {Object.entries(ROTULO_PRIORIDADE).map(([valor, rotulo]) => (
          <option key={valor} value={valor}>
            {rotulo}
          </option>
        ))}
      </CampoDeSelecao>

      {emergencial && (
        <>
          <CampoDeSelecao
            rotulo="Causa da emergência"
            ajuda="Uma das três causas previstas no contrato. Entra no relatório mensal de fiscalização."
            erro={errors.causaEmergencial?.message}
            {...register('causaEmergencial')}
          >
            <option value="">Escolha…</option>
            {Object.entries(ROTULO_CAUSA_EMERGENCIAL).map(([valor, rotulo]) => (
              <option key={valor} value={valor}>
                {rotulo}
              </option>
            ))}
          </CampoDeSelecao>

          <CampoDeTextoLongo
            rotulo="Por que não pode esperar"
            ajuda="O que acontece se esta mudança aguardar a reunião ordinária do comitê."
            erro={errors.justificativaEmergencial?.message}
            rows={3}
            {...register('justificativaEmergencial')}
          />
        </>
      )}

      <CampoDeTexto
        rotulo={ROTULO_DO_CHAMADO}
        // A invariante da transição para o comitê não olha o tipo da mudança: projeto sem chamado
        // também é barrado. Dizer isso aqui, e não só na conferência, evita a ida ao e-Niterói
        // depois de o formulário inteiro estar preenchido.
        ajuda={
          'Número do atendimento no e-Niterói. Apenas dígitos e separadores. Sem ele a solicitação não ' +
          'pode ser enviada ao comitê, mesmo quando a mudança nasce de um projeto.'
        }
        erro={errors.chamadoNumero?.message}
        inputMode="numeric"
        {...register('chamadoNumero')}
      />

      {/* Ao lado do chamado: os dois falam do problema que a mudança atende. Sem opção pré-marcada —
          "definitiva" como padrão faria o paliativo passar por definitivo por omissão. */}
      <CampoDeSelecao
        rotulo={NATUREZA_DA_SOLUCAO.pergunta}
        ajuda={NATUREZA_DA_SOLUCAO.ajuda}
        erro={errors.naturezaDaSolucao?.message}
        defaultValue=""
        {...register('naturezaDaSolucao')}
      >
        <option value="" disabled>
          Escolha…
        </option>
        {Object.entries(NATUREZA_DA_SOLUCAO.opcoes).map(([valor, rotulo]) => (
          <option key={valor} value={valor}>
            {rotulo}
          </option>
        ))}
      </CampoDeSelecao>
    </div>
  )
}
